import React, { createContext, useContext, useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';

// Use relative URL when on same domain (Vercel), or REACT_APP_BACKEND_URL for local dev
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AuthContext = createContext(null);

// An account that exists to follow somebody else's applications. Having
// accepted an invite is what settles it: those accounts open straight onto the
// desk shared with them and never see the School app — a parent handed an empty
// Projects tab is just confusing. Some parents signed up before invites existed
// and may have stray coursework rows on their account; an accepted invite still
// makes them a parent.
export const isGuestAccount = (user) => !!user?.sharedDesks?.length;

// Where a signed-in account belongs when it lands on the root.
export const homePathFor = (user) => {
  if (isGuestAccount(user)) {
    return user.sharedDesks.length === 1 ? `/shared/${user.sharedDesks[0].grantId}` : '/shared';
  }
  return '/hub';
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('ib_token'));
  const [loading, setLoading] = useState(true);
  const isCheckingAuth = useRef(false);

  // Create a stable API instance that updates when token changes
  const api = useMemo(() => {
    return axios.create({
      baseURL: `${API_URL}/api`,
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
  }, [token]);

  // Check auth on mount and when token changes
  useEffect(() => {
    const checkAuth = async () => {
      // Prevent duplicate calls
      if (isCheckingAuth.current) return;
      
      const storedToken = localStorage.getItem('ib_token');
      if (storedToken) {
        isCheckingAuth.current = true;
        try {
          // Create a fresh instance with the stored token for this specific call
          const authApi = axios.create({
            baseURL: `${API_URL}/api`,
            headers: { Authorization: `Bearer ${storedToken}` }
          });
          const res = await authApi.get('/auth/me');
          setUser(res.data.user);
        } catch (err) {
          localStorage.removeItem('ib_token');
          setToken(null);
          setUser(null);
        } finally {
          isCheckingAuth.current = false;
        }
      }
      setLoading(false);
    };
    checkAuth();
    // Only run on mount - token changes are handled by login/logout
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password) => {
    // Create instance without token for login
    const authApi = axios.create({
      baseURL: `${API_URL}/api`
    });
    const res = await authApi.post('/auth/login', { email, password });
    localStorage.setItem('ib_token', res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const register = async (email, password, name, timezone) => {
    // Create instance without token for register
    const authApi = axios.create({
      baseURL: `${API_URL}/api`
    });
    const res = await authApi.post('/auth/register', { email, password, name, timezone });
    localStorage.setItem('ib_token', res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem('ib_token');
    setToken(null);
    setUser(null);
  };

  const updateProfile = async (data) => {
    const res = await api.patch('/auth/profile', data);
    setUser(res.data.user);
    return res.data;
  };

  return (
    <AuthContext.Provider
      value={{ user, token, loading, login, register, logout, updateProfile, api, isGuest: isGuestAccount(user) }}
    >
      {children}
    </AuthContext.Provider>
  );
};
